import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ReviewsModerationTable } from "@/components/admin/reviews/ReviewsModerationTable";
import type { TherapistReview, ReviewStatus } from "@/types";
import { throwIfNotOk } from "@/lib/api-error";
import { toast } from "@/lib/toast";

export const Route = createFileRoute("/dashboard/admin/reviews")({
  component: AdminReviewsPage,
});

const tabs: ReviewStatus[] = ["pending", "approved", "rejected", "hidden"];
const labels: Record<ReviewStatus, string> = {
  pending: "Na čekanju",
  approved: "Odobrene",
  rejected: "Odbijene",
  hidden: "Skrivene",
};

function AdminReviewsPage() {
  const [tab, setTab] = useState<ReviewStatus>("pending");
  const [items, setItems] = useState<TherapistReview[]>([]);

  async function load(status: ReviewStatus) {
    try {
      const res = await fetch(`/api/admin/reviews?status=${status}`);
      await throwIfNotOk(res, "Greška pri učitavanju recenzija.");
      const body = await res.json();
      setItems(body.items);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Greška pri učitavanju recenzija.");
    }
  }
  useEffect(() => { load(tab); }, [tab]);

  // Shows the server's message and rethrows, so the table keeps the reject /
  // edit form open on a 400 instead of closing as if it had worked.
  async function send(
    method: "POST" | "PATCH",
    url: string,
    body: unknown,
    fallback: string,
    success: string,
  ) {
    try {
      const res = await fetch(url, {
        method,
        headers: body === undefined ? undefined : { "content-type": "application/json" },
        body: body === undefined ? undefined : JSON.stringify(body),
      });
      await throwIfNotOk(res, fallback);
      toast.success(success);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : fallback);
      throw err;
    }
    await load(tab);
  }

  const approve = (id: string) =>
    send("POST", `/api/admin/reviews/${id}/approve`, undefined, "Greška pri odobravanju recenzije.", "Recenzija odobrena");
  const reject = (id: string, reason: string) =>
    send("POST", `/api/admin/reviews/${id}/reject`, { rejectionReason: reason }, "Greška pri odbijanju recenzije.", "Recenzija odbijena");
  const hide = (id: string) =>
    send("POST", `/api/admin/reviews/${id}/hide`, undefined, "Greška pri skrivanju recenzije.", "Recenzija skrivena");
  const edit = (id: string, patch: { reviewText?: string; rating?: number }) =>
    send("PATCH", `/api/admin/reviews/${id}`, patch, "Greška pri spremanju recenzije.", "Recenzija spremljena");

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold">Moderacija recenzija</h1>
      <nav className="flex gap-1 border-b border-neutral-200">
        {tabs.map((t) => (
          <button key={t} onClick={() => setTab(t)} className={`px-3 py-2 text-sm ${tab === t ? "border-b-2 border-neutral-900 font-medium" : "text-neutral-500"}`}>
            {labels[t]}
          </button>
        ))}
      </nav>
      <ReviewsModerationTable items={items} onApprove={approve} onReject={reject} onHide={hide} onEdit={edit} />
    </div>
  );
}
